import { LECTERN, type ModelAnimation } from '@vrlearning/lesson-schema';
import { BEKIJKEN_TEXT, clipLabel } from './text.js';

/**
 * The Bekijken panel (ADR-058), as data: the item's name; a row per feature, its name then its states
 * (the current one lit; at most three to a row; a second row named again when it opens a page), or
 * "Stil" and the clips (four to a row); and Sluiten, with ◀ ▶ beside it when the rows need more than
 * one page. In the lectern panel's place and plane,
 * rows going down from its top; never more than five rows, under the lectern's 0.78 m. Drawn by
 * render/bekijken-panel.ts; tested here, where it can be.
 */
export const BEKIJKEN_PANEL = {
  width: LECTERN.width,
  rowHeight: 0.12,
  gap: 0.02,
  columns: 4,
  /** Feature or clip rows to a page: with the name and the Sluiten row, five rows. */
  contentRows: 3,
} as const;

export type BekijkenAction =
  | { kind: 'state'; feature: string; state: string }
  | { kind: 'clip'; clip: ModelAnimation | null }
  | { kind: 'page'; page: number }
  | { kind: 'close' }
  | { kind: 'none' };

export type BekijkenButton = {
  label: string;
  action: BekijkenAction;
  x: number;
  y: number;
  width: number;
  /** The state shown now, or the clip playing: lit. */
  current: boolean;
};

export type BekijkenPanelInput = {
  title: string;
  features: Readonly<Record<string, { readonly name: string; readonly states: readonly string[] }>>;
  states: ReadonlyMap<string, string>;
  clips: readonly ModelAnimation[];
  playing: ModelAnimation | null;
  page: number;
};

type Cell = { label: string; action: BekijkenAction; current: boolean };
const blank: Cell = { label: '', action: { kind: 'none' }, current: false };
/** A feature's second row: blank under its name, the name again when it opens a page. */
type Row = Cell[] & { continues?: string };

function rowsOf(input: BekijkenPanelInput): Row[] {
  const { columns } = BEKIJKEN_PANEL;
  const rows: Row[] = [];
  for (const [feature, entry] of Object.entries(input.features)) {
    const states = entry.states.map((state): Cell => ({
      label: state,
      action: { kind: 'state', feature, state },
      current: input.states.get(feature) === state,
    }));
    for (let at = 0; at < states.length; at += columns - 1) {
      const label: Cell =
        at === 0 ? { label: entry.name, action: { kind: 'none' }, current: false } : blank;
      const row: Row = [label, ...states.slice(at, at + columns - 1)];
      if (at > 0) row.continues = entry.name;
      rows.push(row);
    }
  }
  if (input.clips.length > 0) {
    const cells: Cell[] = [
      {
        label: BEKIJKEN_TEXT.still,
        action: { kind: 'clip', clip: null },
        current: input.playing === null,
      },
      ...input.clips.map((clip): Cell => ({
        label: clipLabel(clip),
        action: { kind: 'clip', clip },
        current: input.playing === clip,
      })),
    ];
    for (let at = 0; at < cells.length; at += columns) rows.push(cells.slice(at, at + columns));
  }
  return rows;
}

export function bekijkenPages(input: BekijkenPanelInput): number {
  return Math.max(1, Math.ceil(rowsOf(input).length / BEKIJKEN_PANEL.contentRows));
}

export function bekijkenButtons(input: BekijkenPanelInput): BekijkenButton[] {
  const { width, rowHeight, gap, columns, contentRows } = BEKIJKEN_PANEL;
  const cell = (width - gap * (columns - 1)) / columns;
  const step = rowHeight + gap;
  const xOf = (column: number): number => -width / 2 + cell / 2 + column * (cell + gap);
  const rows = rowsOf(input);
  const pages = Math.max(1, Math.ceil(rows.length / contentRows));
  const page = Math.min(Math.max(input.page, 0), pages - 1);
  const shown = rows.slice(page * contentRows, (page + 1) * contentRows);

  const buttons: BekijkenButton[] = [
    { label: input.title, action: { kind: 'none' }, x: 0, y: 0, width, current: false },
  ];
  shown.forEach((row, r) => {
    row.forEach((c, column) => {
      // Nine features can split one over two pages (21 Oct 2026): a page never opens on nameless states.
      const shownCell =
        c === blank && column === 0 && r === 0 && row.continues !== undefined
          ? { ...blank, label: row.continues }
          : c;
      if (shownCell === blank) return;
      buttons.push({ ...shownCell, x: xOf(column), y: -(r + 1) * step, width: cell });
    });
  });
  const y = -(shown.length + 1) * step;
  const close: BekijkenButton = {
    label: BEKIJKEN_TEXT.close,
    action: { kind: 'close' },
    x: 0,
    y,
    width: cell * 2 + gap,
    current: false,
  };
  if (pages === 1) return [...buttons, close];
  return [
    ...buttons,
    {
      label: '◀',
      action: { kind: 'page', page: (page - 1 + pages) % pages },
      x: xOf(0),
      y,
      width: cell,
      current: false,
    },
    close,
    {
      label: '▶',
      action: { kind: 'page', page: (page + 1) % pages },
      x: xOf(columns - 1),
      y,
      width: cell,
      current: false,
    },
  ];
}

/** Where the panel's first row is, in the lectern's own plane: the lectern panel's top row. */
export function bekijkenPanelAt(): { x: number; y: number } {
  return { x: 0, y: LECTERN.panelY + LECTERN.height / 2 - BEKIJKEN_PANEL.rowHeight / 2 };
}
